import { useState, useMemo, useEffect } from 'react';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';
import { Table } from 'react-bootstrap';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { OrdersTablePaging } from './OrdersTablePaging';
import { OrdersTableFilterControl } from './OrdersTableFilterControl';
import { ParxlTemplateModal } from './ParxlTemplateModal';
import type { Column } from './Columns';
import { Columns as allColumns } from './Columns';

interface OrdersTableProps {
  orders: OrderSummary[];
  isLoading?: boolean;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
}

type SortDirection = 'asc' | 'desc' | null;
type SortConfig = {
  key: string;
  direction: SortDirection;
};

export function OrdersTable({ orders, isLoading, startDate, endDate, onDateRangeChange }: OrdersTableProps) {
  const location = useLocation();
  const { state } = location;

  const [searchTerm, setSearchTerm] = useState(() => {
    if (state?.searchTerm) {
      return state.searchTerm;
    }
    return localStorage.getItem('searchTerm') || '';
  });
  const [currentPage, setCurrentPage] = useState(state?.page || 1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    return localStorage.getItem('itemsPerPage') || '15';
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'orderId', direction: 'desc' });
  const [visibleColumns, setVisibleColumns] = useState<Column[]>(allColumns.filter((col) => col.isVisible));
  const [orderDate, setOrderDate] = useState(() => {
    if (state?.orderDate) {
      return state.orderDate;
    }
    return localStorage.getItem('orderDate') || '';
  });
  const [country, setCountry] = useState(() => {
    if (state?.country) {
      return state.country;
    }
    return localStorage.getItem('country') || '';
  });
  const [status, setStatus] = useState(() => {
    if (state?.status) {
      return state.status;
    }
    return localStorage.getItem('status') || '';
  });
  const [pic, setPic] = useState(() => {
    if (state?.pic) {
      return state.pic;
    }
    return localStorage.getItem('pic') || '';
  });
  const [hasOrderNumber, setHasOrderNumber] = useState(() => {
    const stored = localStorage.getItem('hasOrderNumber');
    return stored === 'true';
  });
  const [hasTrackingNumber, setHasTrackingNumber] = useState(() => {
    const stored = localStorage.getItem('hasTrackingNumber');
    return stored === 'true';
  });
  const [showParxlModal, setShowParxlModal] = useState(false);

  useEffect(() => {
    console.log('Location state changed:', state);

    if (state) {
      if (state.page) {
        setCurrentPage(state.page);
      }
      if (state.searchTerm) {
        setSearchTerm(state.searchTerm);
        localStorage.setItem('searchTerm', state.searchTerm);
      }
      if (state.orderDate) {
        setOrderDate(state.orderDate);
        localStorage.setItem('orderDate', state.orderDate);
      }
      if (state.country) {
        setCountry(state.country);
        localStorage.setItem('country', state.country);
      }
      if (state.status) {
        setStatus(state.status);
        localStorage.setItem('status', state.status);
      }
      if (state.pic) {
        setPic(state.pic);
        localStorage.setItem('pic', state.pic);
      }
    }
  }, [state]);

  const handleColumnVisibility = (column: Column, isVisible: boolean) => {
    if (column.isRequired) {
      return;
    } // Don't allow hiding required columns

    setVisibleColumns((prev) => {
      if (isVisible) {
        // If column is not already visible, add it
        if (!prev.some((col) => col.accessor === column.accessor)) {
          // Find the position in allColumns to maintain order
          const allVisibleColumns = allColumns.filter(
            (col) =>
              col.isRequired || prev.some((p) => p.accessor === col.accessor) || col.accessor === column.accessor,
          );
          return allVisibleColumns;
        }
        return prev;
      } else {
        // Remove the column if it exists
        return prev.filter((col) => col.accessor !== column.accessor);
      }
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    localStorage.setItem('searchTerm', value);
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    localStorage.setItem('country', value);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    localStorage.setItem('status', value);
  };

  const handlePicChange = (value: string) => {
    setPic(value);
    localStorage.setItem('pic', value);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(value);
    localStorage.setItem('itemsPerPage', value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleHasOrderNumberChange = (value: boolean) => {
    setHasOrderNumber(value);
    localStorage.setItem('hasOrderNumber', String(value));
    setCurrentPage(1);
  };

  const handleHasTrackingNumberChange = (value: boolean) => {
    setHasTrackingNumber(value);
    localStorage.setItem('hasTrackingNumber', String(value));
    setCurrentPage(1);
  };

  const filteredOrders = useMemo(() => {
    let result = orders;

    // Apply date range filter
    if (startDate && endDate) {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      // Set endDateObj to end of day for inclusive filtering
      endDateObj.setHours(23, 59, 59, 999);

      result = result.filter((order) => {
        const orderDateObj = new Date(order.orderDate);
        return orderDateObj >= startDateObj && orderDateObj <= endDateObj;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          Object.values(order).some((value) => String(value).toLowerCase().includes(term)) ||
          (order.deleoData?.deleoOrderId && String(order.deleoData.deleoOrderId).toLowerCase().includes(term)) ||
          (order.customer.name && String(order.customer.name).toLowerCase().includes(term)) ||
          (order.customer.email && String(order.customer.email).toLowerCase().includes(term)) ||
          (order.lineItems &&
            order.lineItems.some((item) => item.sku && String(item.sku).toLowerCase().includes(term))),
      );
    }

    // Apply order date filter
    if (orderDate) {
      const selectedDay = parseInt(orderDate);

      result = result.filter((order) => {
        const orderDate = new Date(order.orderDate);
        return orderDate.getDate() === selectedDay;
      });
    }

    // Apply country filter
    if (country && country !== 'Select Country') {
      result = result.filter((order) => order.consignee.countryCode === country);
    }

    // Apply status filter
    if (status && status !== 'Select Status') {
      result = result.filter((order) => order.status.toLowerCase() === status.toLowerCase());
    }

    // Apply pic filter
    if (pic) {
      result = result.filter((order) => {
        return order.pic?.toLowerCase().includes(pic.toLowerCase());
      });
    }

    // Apply hasOrderNumber filter
    if (hasOrderNumber) {
      result = result.filter((order) => {
        return order.lineItems.some((item) => item.sourcingShipmentTrack?.orderNumber);
      });
    }

    // Apply hasTrackingNumber filter
    if (hasTrackingNumber) {
      result = result.filter((order) => {
        return order.lineItems.some((item) => item.sourcingShipmentTrack?.trackingNumber);
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (sortConfig.key === 'orderId') {
          const aValue = parseInt(a[sortConfig.key]);
          const bValue = parseInt(b[sortConfig.key]);
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (sortConfig.key === 'orderDate') {
          const aValue = new Date(a[sortConfig.key]).getTime();
          const bValue = new Date(b[sortConfig.key]).getTime();
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        return 0;
      });
    }

    return result;
  }, [
    orders,
    startDate,
    endDate,
    searchTerm,
    orderDate,
    country,
    status,
    pic,
    hasOrderNumber,
    hasTrackingNumber,
    sortConfig,
  ]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredOrders.length / Number(itemsPerPage));
  const startIndex = (currentPage - 1) * Number(itemsPerPage);
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + Number(itemsPerPage));

  const handleSort = (key: string) => {
    let direction: SortDirection = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null;
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
    }
    return <ChevronUp className="h-4 w-4" style={{ opacity: 0.3 }} />;
  };

  // Format date range for display
  const formatDateRange = () => {
    if (!startDate || !endDate) {
      return '';
    }
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return `${start} to ${end}`;
  };

  return (
    <div className="space-y-4 flex flex-col items-center">
      <OrdersTableFilterControl
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        allColumns={allColumns}
        visibleColumns={visibleColumns}
        onColumnVisibilityChange={handleColumnVisibility}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={onDateRangeChange}
        country={country}
        onCountryChange={handleCountryChange}
        status={status}
        onStatusChange={handleStatusChange}
        pic={pic}
        onPicChange={handlePicChange}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        hasOrderNumber={hasOrderNumber}
        onHasOrderNumberChange={handleHasOrderNumberChange}
        hasTrackingNumber={hasTrackingNumber}
        onHasTrackingNumberChange={handleHasTrackingNumberChange}
        onOpenParxlModal={() => setShowParxlModal(true)}
      />

      <ParxlTemplateModal show={showParxlModal} onHide={() => setShowParxlModal(false)} orders={filteredOrders} />

      {/* Show order count with date range */}
      <p className="text-muted-foreground p-2">
        {filteredOrders.length} orders in range: {formatDateRange()}
      </p>

      <div className="rounded-md border mt-3 w-full">
        <Table hover responsive className="align-middle mb-0">
          <TableHeader>
            <TableRow className="h-12">
              {visibleColumns.map((column) => (
                <TableHead
                  key={String(column.accessor)}
                  className="p-0 cursor-pointer select-none"
                  onClick={() => handleSort(String(column.accessor))}
                  style={column.accessor === 'lineItems.sourcingInfo' ? { width: '300px' } : undefined}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      {column.header}
                      {getSortIcon(String(column.accessor))}
                    </div>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="h-12 text-center py-3">
                  Loading...
                </TableCell>
              </TableRow>
            ) : paginatedOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="h-12 text-center py-3">
                  No orders found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedOrders.map((order) => (
                <TableRow key={order.orderId} className="hover:bg-muted/50 h-12">
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={`${order.orderId}-${String(column.accessor)}`}
                      className="py-3"
                      style={column.accessor === 'lineItems.sourcingInfo' ? { width: '300px' } : undefined}
                    >
                      {column.cell
                        ? column.cell(order, { hasOrderNumber, hasTrackingNumber })
                        : (order as any)[column.accessor]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <OrdersTablePaging currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
}
